import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import { resetAuthSessionDiagnostics } from 'src/store/auth/refreshAccessToken.js';
import * as Yup from 'yup';

import { Form, formResolver, RHFPasswordField, RHFTextField } from '@components/Form';
import { useSignIn } from '@hooks/users';
import useStoreAuth from '@hooks/useStoreAuth';
// Material UI
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { DEFAULT_WEEK_START } from '@utils/userConfig';

interface LoginFormValues {
  email: string;
  password: string;
}

const defaultValues: LoginFormValues = {
  email: '',
  password: '',
};

function buildLoginSchema(requiredMessage: string): Yup.ObjectSchema<LoginFormValues> {
  return Yup.object().shape({
    email: Yup.string()
      .email('Wrong email format')
      .min(1, 'Minimum 1 symbols')
      .max(250, 'Maximum 250 symbols')
      .required(requiredMessage),
    password: Yup.string()
      .min(1, 'Minimum 1 symbols')
      .max(250, 'Maximum 250 symbols')
      .required(requiredMessage),
  }) as Yup.ObjectSchema<LoginFormValues>;
}

// Shape of the error payload returned by the auth backend on a failed sign-in.
interface AuthErrorResponse {
  response?: {
    data?: {
      detail?: string;
      error_description?: string;
    };
  };
}

function extractErrorMessage(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }

  const { response } = error as AuthErrorResponse;
  return response?.data?.detail ?? response?.data?.error_description;
}

export default function Login(): JSX.Element {
  const intl = useIntl();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { signIn } = useSignIn();
  const [, { setAuth }] = useStoreAuth();

  const requiredMessage = intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' });
  const loginSchema = useMemo(() => buildLoginSchema(requiredMessage), [requiredMessage]);

  const methods = useForm<LoginFormValues>({
    defaultValues,
    resolver: formResolver<LoginFormValues>(loginSchema),
    mode: 'onTouched',
  });
  const {
    formState: { isSubmitting },
  } = methods;

  const onSubmit = async (values: LoginFormValues): Promise<void> => {
    setErrorMessage(null);
    try {
      const { access, refresh, scopes, role, userId, canImpersonate } = await signIn({
        username: values.email,
        password: values.password,
      });

      // New session: re-arm the once-per-session auth diagnostics the previous
      // session in this tab may already have spent.
      resetAuthSessionDiagnostics();

      setAuth({
        token: access,
        refresh,
        scopes,
        role,
        canImpersonate,
        userId,
        usersGridConfig: null,
        segmentationTypes: null,
        weekStart: DEFAULT_WEEK_START,
      });
    } catch (error: unknown) {
      setErrorMessage(
        extractErrorMessage(error) ??
          intl.formatMessage({
            id: 'AUTH.VALIDATION.INVALID_LOGIN',
          })
      );
    }
  };

  return (
    <Box sx={{ textAlign: 'center', mb: 5, minWidth: { xs: '100%', sm: 400 }, mx: 'auto' }}>
      <Stack sx={{ textAlign: 'center', mb: 5 }}>
        <Typography variant="h3">
          <FormattedMessage id="AUTH.LOGIN.TITLE" />
        </Typography>
        <Typography color="text.secondary" variant="body1">
          Enter your username and password
        </Typography>
      </Stack>

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ width: '100%', maxWidth: 520 }}>
          <Form<LoginFormValues> methods={methods} onSubmit={onSubmit}>
            {errorMessage && (
              <Box
                data-testid="login-error"
                sx={{ mb: 3, bgcolor: 'error.light', p: 2, borderRadius: 1 }}
              >
                <Box sx={(theme) => ({ fontWeight: theme.typography.fontWeightBold })}>
                  {errorMessage}
                </Box>
              </Box>
            )}

            <Box sx={{ mb: 2 }}>
              <RHFTextField<LoginFormValues>
                fullWidth
                name="email"
                placeholder="Email"
                size="medium"
                type="email"
                variant="outlined"
              />
            </Box>

            <Box sx={{ mb: 2, minWidth: { xs: '100%', sm: 400 } }}>
              <RHFPasswordField<LoginFormValues>
                fullWidth
                name="password"
                placeholder="Password"
                size="medium"
                variant="outlined"
              />
            </Box>

            <Stack
              alignItems="center"
              direction="row"
              justifyContent="space-between"
              sx={{ mt: 1 }}
            >
              <Box>
                <RouterLink className="text-dark-50 text-hover-primary" to="/auth/forgot-password">
                  <FormattedMessage id="AUTH.GENERAL.FORGOT_BUTTON" />
                </RouterLink>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  color="primary"
                  disabled={isSubmitting}
                  id="kt_login_signin_submit"
                  sx={(theme) => ({
                    fontWeight: theme.typography.fontWeightBold,
                    px: 4,
                  })}
                  type="submit"
                  variant="contained"
                >
                  <Box component="span">Sign In</Box>
                  {isSubmitting && <CircularProgress size={18} sx={{ color: 'white', ml: 1 }} />}
                </Button>
              </Box>
            </Stack>
          </Form>
        </Box>
      </Box>
    </Box>
  );
}
